import { CustomTableConfig } from 'commons/components';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'core/i18n/i18n';
import { EditableColumn } from 'modules/global/model';
import { TrackedEventsCell } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-custom-alarm-list/components/tracked-events-cell/tracked-events-cell.component';
import { CustomAlarmListItem } from 'modules/settings/pods/settings-devices/settings-devices-configuration/settings-devices-custom-alarm-list/settings-devices-custom-alarm-list.model';

export const getCustomAlarmHeaderColumn = (column: string) => `settings.devices.custom-alarms.list.columns.${column}`;

export const getCustomAlarmsColumnsToShow = (t: (key: string) => string): EditableColumn[] => [
    {
        field: 'notificationName',
        text: t(getCustomAlarmHeaderColumn('name')),
        visible: true,
        pinned: false,
        columnIndex: 0,
    },
    {
        field: 'trackedEvents',
        text: t(getCustomAlarmHeaderColumn('events')),
        visible: true,
        pinned: false,
        columnIndex: 1,
    },
    {
        field: 'trackingPeriodDays',
        text: t(getCustomAlarmHeaderColumn('period')),
        visible: true,
        pinned: false,
        columnIndex: 2,
    },
    {
        field: 'notificationType',
        text: t(getCustomAlarmHeaderColumn('notifications')),
        visible: true,
        pinned: false,
        columnIndex: 3,
    },
    {
        field: 'optionalSettings',
        text: t(getCustomAlarmHeaderColumn('optional')),
        visible: true,
        pinned: false,
        columnIndex: 4,
    },
    {
        field: 'status',
        text: t(getCustomAlarmHeaderColumn('status')),
        visible: true,
        pinned: false,
        columnIndex: 5,
    },
];

export const buildDefaultConfigCustomAlarmsList = (
    t: (key: string) => string
): CustomTableConfig<CustomAlarmListItem> => ({
    entity: 'customNotifications',
    columnsToShow: getCustomAlarmsColumnsToShow(t),
    fields: [
        {
            name: 'notificationName',
            headerOptions: {
                text: t(getCustomAlarmHeaderColumn('name')),
            },
        },
        {
            name: 'trackedEvents',
            headerOptions: {
                text: t(getCustomAlarmHeaderColumn('events')),
                sortable: false,
            },
            rowOptions: {
                cellOptions: {
                    format: (data: CustomAlarmListItem) => <TrackedEventsCell events={data.trackedEvents} />,
                },
            },
        },
        {
            name: 'trackingPeriodDays',
            headerOptions: {
                text: t(getCustomAlarmHeaderColumn('period')),
                sortable: false,
            },
        },
        {
            name: 'notificationType',
            headerOptions: {
                text: t(getCustomAlarmHeaderColumn('notifications')),
                sortable: false,
            },
            rowOptions: {
                cellOptions: {
                    format: (data: CustomAlarmListItem) =>
                        data.notificationType
                            .split(', ')
                            .map((key) => t(key))
                            .join(', '),
                },
            },
        },
        {
            name: 'optionalSettings',
            headerOptions: {
                text: t(getCustomAlarmHeaderColumn('optional')),
                sortable: false,
            },
            rowOptions: {
                cellOptions: {
                    format: (data: CustomAlarmListItem) => t(data.optionalSettings),
                },
            },
        },
        {
            name: 'status',
            headerOptions: {
                text: t(getCustomAlarmHeaderColumn('status')),
                sortable: false,
            },
            rowOptions: {
                cellOptions: {
                    format: (data: CustomAlarmListItem) =>
                        t(
                            data.active
                                ? 'settings.devices.custom-alarms.list.status.active'
                                : 'settings.devices.custom-alarms.list.status.inactive'
                        ),
                },
            },
        },
    ],
});

export const useDefaultConfigCustomAlarmsList = () => {
    const { t } = useTranslation();

    const config = useMemo<CustomTableConfig<CustomAlarmListItem>>(() => buildDefaultConfigCustomAlarmsList(t), [t]);

    const getDefaultConfig = useCallback((): CustomTableConfig<CustomAlarmListItem> => config, [config]);

    return { getDefaultConfig };
};
